import { useRef, useState } from "react";
import { api, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, DotsSixVertical } from "@phosphor-icons/react";

// Multi-upload + drag/drop reordering. Used by admin ListingForm.
export default function ImageUploader({ images = [], onChange, testId = "image-uploader" }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(null);

  async function upload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(data.path);
      }
      onChange([...(images || []), ...uploaded]);
      toast.success(`Uploaded ${uploaded.length} image(s)`);
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  }

  function remove(i) { onChange(images.filter((_, idx) => idx !== i)); }
  function move(from, to) {
    if (from === to) return;
    const copy = [...images];
    const [item] = copy.splice(from, 1);
    copy.splice(to, 0, item);
    onChange(copy);
  }

  return (
    <div data-testid={testId}>
      <div className="flex flex-wrap gap-3 mb-3">
        {(images || []).map((im, i) => (
          <div
            key={`${im}-${i}`}
            draggable
            onDragStart={() => setDragging(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { if (dragging !== null) move(dragging, i); setDragging(null); }}
            className={`relative w-36 h-28 bg-gray-100 border ${dragging === i ? "border-[#001588]" : "border-gray-200"} cursor-grab active:cursor-grabbing`}
            data-testid={`img-${i}`}
          >
            <img src={resolveImage(im)} alt="" className="w-full h-full object-cover" />
            <div className="absolute top-1 left-1 bg-[#001588] text-white text-[10px] px-1.5 py-0.5">{i === 0 ? "COVER" : i + 1}</div>
            <button
              type="button" onClick={() => remove(i)}
              className="absolute top-1 right-1 w-6 h-6 bg-white/95 text-white flex items-center justify-center"
              data-testid={`img-remove-${i}`}
            >
              <X size={12} />
            </button>
            <div className="absolute bottom-1 right-1 bg-[#001588] text-white p-0.5">
              <DotsSixVertical size={12} />
            </div>
          </div>
        ))}
      </div>
      <input ref={fileRef} type="file" accept="image/*" multiple onChange={upload} className="hidden" data-testid="img-input" />
      <button
        type="button" onClick={() => fileRef.current?.click()} disabled={busy}
        className="flex items-center gap-2 border border-gray-300 px-4 py-2 text-sm hover:border-black disabled:opacity-50"
        data-testid="img-upload-btn"
      >
        <Upload size={14} /> {busy ? "Uploading…" : "Upload images"}
      </button>
      <p className="text-xs text-gray-500 mt-2">Drag to reorder. First image becomes the cover on listings & OG previews.</p>
    </div>
  );
}
